import type { ComponentType } from 'react';
import { useState } from 'react';

import { Tabs } from 'cpool-components/general/Tabs';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { useTranslation } from 'react-i18next';
import { LuChevronLeft, LuChevronRight, LuTrash2 } from 'react-icons/lu';

import {
    AddressAndPropertyTypeStep,
    HeatingHotWaterStep,
    RenovationsStep,
    RoofBasementWindowsStep,
    SanitaryParkingStep,
} from './Steps';
import {
    BUILDING_DESCRIPTION_STEP_IDS,
    type BuildingDescriptionStepId,
    DEFAULT_BUILDING_DESCRIPTION_STEP_ID,
} from './types';

type Props = {
    disabled?: boolean;
    onRemove: () => void;
    onSelectedStepChange?: (step: BuildingDescriptionStepId) => void;
    realEstateIndex: number;
    selectedStep?: BuildingDescriptionStepId;
};

type StepProps = Pick<Props, 'disabled' | 'realEstateIndex'>;

const STEP_COMPONENTS: Record<BuildingDescriptionStepId, ComponentType<StepProps>> = {
    ADDRESS: AddressAndPropertyTypeStep,
    HEATING: HeatingHotWaterStep,
    RENOVATIONS: RenovationsStep,
    ROOF: RoofBasementWindowsStep,
    SANITARY: SanitaryParkingStep,
};

export const BuildingDescription: React.FC<Props> = ({
    disabled,
    onRemove,
    onSelectedStepChange,
    realEstateIndex,
    selectedStep,
}) => {
    const { t } = useTranslation();
    const [internalStep, setInternalStep] = useState<BuildingDescriptionStepId>(DEFAULT_BUILDING_DESCRIPTION_STEP_ID);
    const activeStep = selectedStep ?? internalStep;
    const selectedStepIndex = BUILDING_DESCRIPTION_STEP_IDS.indexOf(activeStep);
    const SelectedStep = STEP_COMPONENTS[activeStep];
    const stepProps = { disabled, realEstateIndex };
    const panelIdPrefix = `building-description-panel-${realEstateIndex}`;
    const changeStep = (step: BuildingDescriptionStepId) => {
        if (selectedStep === undefined) setInternalStep(step);
        onSelectedStepChange?.(step);
    };

    return (
        <section className="mt-4 space-y-4">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center rounded bg-surface-light px-1 py-1">
                <span aria-hidden="true" />
                <h6 className="text-sm font-bold text-midnight">{t('REAL_ESTATE.BUILDING_DESCRIPTION.TITLE')}</h6>
                <Button
                    type="button"
                    variant={ButtonVariant.SECONDARY}
                    iconLeft={<LuTrash2 />}
                    onClick={onRemove}
                    disabled={disabled}
                    className="justify-self-end py-1"
                >
                    {t('REAL_ESTATE.BUILDING_DESCRIPTION.REMOVE')}
                </Button>
            </div>
            <Tabs
                tabs={BUILDING_DESCRIPTION_STEP_IDS.map((id) => ({
                    id,
                    label: t(`REAL_ESTATE.BUILDING_DESCRIPTION.STEPS.${id}`),
                }))}
                selectedId={activeStep}
                onSelect={(id) => changeStep(id as BuildingDescriptionStepId)}
                listClassName="flex w-full"
                panelIdPrefix={panelIdPrefix}
            />
            <div
                id={`${panelIdPrefix}-${activeStep}`}
                role="tabpanel"
                aria-label={t(`REAL_ESTATE.BUILDING_DESCRIPTION.STEPS.${activeStep}`)}
            >
                <SelectedStep {...stepProps} />
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-t border-grey-outline-light pt-4">
                {selectedStepIndex === 0 ? (
                    <span aria-hidden="true" />
                ) : (
                    <Button
                        type="button"
                        variant={ButtonVariant.SECONDARY}
                        iconLeft={<LuChevronLeft />}
                        onClick={() => changeStep(BUILDING_DESCRIPTION_STEP_IDS[selectedStepIndex - 1])}
                        className="justify-self-start"
                    >
                        {t('BUTTONS.BACK')}
                    </Button>
                )}
                <span className="text-center text-sm text-midnight">
                    <span>
                        {t('REAL_ESTATE.BUILDING_DESCRIPTION.STEP_COUNTER', {
                            current: selectedStepIndex + 1,
                            total: BUILDING_DESCRIPTION_STEP_IDS.length,
                        })}
                    </span>
                    {' · '}
                    <span>{t(`REAL_ESTATE.BUILDING_DESCRIPTION.STEPS.${activeStep}`)}</span>
                </span>
                {selectedStepIndex === BUILDING_DESCRIPTION_STEP_IDS.length - 1 ? (
                    <span aria-hidden="true" />
                ) : (
                    <Button
                        type="button"
                        variant={ButtonVariant.PRIMARY}
                        iconRight={<LuChevronRight />}
                        onClick={() => changeStep(BUILDING_DESCRIPTION_STEP_IDS[selectedStepIndex + 1])}
                        className="justify-self-end"
                    >
                        {t('PAGINATION.NEXT')}
                    </Button>
                )}
            </div>
        </section>
    );
};
